import {ValueOf} from 'type-fest';

import {impersonationToken} from 'core/impersonation';
import {DATE_FORMAT_ISO8601_LOCAL, dayjs} from 'utils/dateUtils';

const IMPERSONATION_TOKEN_MAP_KEY = 'impersonated_tokens_map';
// Sync with https://github.com/ennabl/authenticator/blob/e6f5e3d6b43671ad53a64b39ee954383fd065936/lib/impersonation/impersonation.ts#L13
const IMPERSONATION_TOKEN_EXPIRES_IN_DAYS = 1;
type ImpersonatedTokensMap = Record<string, Date>;

const formatImpersonatedKey = (token: string | undefined, key: LocalStorageKey) => {
  return `${token}/${key}`;
};

const trackImpersonatedSession = () => {
  if (!impersonationToken) {
    return;
  }

  const map = localStorage.getItem(IMPERSONATION_TOKEN_MAP_KEY);
  const json = (map ? JSON.parse(map) : {}) as ImpersonatedTokensMap;

  if (impersonationToken in json) {
    return;
  }

  localStorage.setItem(
    IMPERSONATION_TOKEN_MAP_KEY,
    JSON.stringify({
      ...json,
      [impersonationToken]: dayjs().format(DATE_FORMAT_ISO8601_LOCAL),
    })
  );
};

const clearExpiredImpersonatedTokens = () => {
  if (!impersonationToken) {
    return;
  }

  const map = localStorage.getItem(IMPERSONATION_TOKEN_MAP_KEY);

  if (!map) {
    return;
  }

  const json = JSON.parse(map) as ImpersonatedTokensMap;

  Object.entries(json).forEach(([token, date]) => {
    if (!dayjs(date).add(IMPERSONATION_TOKEN_EXPIRES_IN_DAYS, 'days').isBefore(dayjs())) {
      return;
    }

    Object.values(localStorageKeyMap)
      .filter(key => !universalKeys.includes(key))
      .forEach(key => {
        localStorage.removeItem(formatImpersonatedKey(token, key));
      });

    delete json[token];
  });

  localStorage.setItem(IMPERSONATION_TOKEN_MAP_KEY, JSON.stringify(json));
};

const localStorageKeyMap = {
  AIWelcome: 'ai_welcome',
  BizOrgsByCarrierDefaultDepartmentLevel: 'biz_orgs_by_carrier_default_department_level',
  CarrierAppetiteStore: 'carrier_appetite_store',
  ChatsStore: 'chats_store',
  CypressToken: 'auth0_token_cypress',
  CypressExpiresIn: 'auth0_expires_in_cypress',
  CustomHomeDashboards: 'custom_home_dashboards',
  CustomLimitValue: 'custom_limit_value',
  DecisionsRecentQueries: 'decisions_recent_queries',
  DocumentsPage: 'documents_page',
  DocumentsComparePage: 'documents_compare_page',
  DocumentsStore: 'documents_store',
  DownloadMobileAppLastDialogTime: 'download_mobile_app_last_dialog_time',
  EmulateLimitError: 'emulate_limit_error',
  GrowthSidebarListsShowMore: 'growth_sidebar_lists_show_more',
  GrowthSidebarRemindersShowMore: 'growth_sidebar_reminders_show_more',
  GrowthSidebarBoxListShowMore: 'growth_sidebar_box_list_show_more',
  IsLossRunsExpiredAlertHidden: 'is_loss_runs_exceeded_warning_hidden',
  IsNetNewOpportunitiesSearchLimitExceededModalHidden: 'is_net_new_opportunities_search_limit_exceeded_modal_hidden',
  IsNetNewOpportunitiesSearchLimitsWarningHidden: 'is_net_new_opportunities_search_limits_warning_hidden',
  IsRoleEditWarningHidden: 'is_role_edit_warning_hidden',
  IsUserEnvironmentEditWarningHidden: 'is_user_environment_edit_warning_hidden',
  KpiPagePremiumTypeFilter: 'kpi_page_premium_type_filter',
  KpiPageDashboardMap: 'kpi_page_dashboard_map',
  LossRunsListSort: 'loss_runs_list_sort',
  LifetimeValueSnapshotDate: 'lifetime_value_snapshot_date',
  EnableLogRocketOnDev: 'enable_log_rocket_on_dev',
  MarketsCarrierLevelFilterStore: 'markets_carrier_level_filter_store',
  EnnablAIPromo: 'ennabl_ai_promo',
  Modifiers: 'modifiers',
  MonolineHighlights: 'monoline_highlights',
  NotificationDialog: 'notification_dialog',
  O360Score: 'o360_score',
  O360UnisonBook: 'o360_unison_book',
  OpportunitiesCompanyForm5500SearchRecentSearchItems: 'opportunities_company_form5500_search_recent_search_items',
  OpportunitiesCompanySearchByIndustryCountryCode: 'opportunities_company_search_by_industry_country_code',
  OpportunitiesCompanySearchByIndustryRecentSearchItems: 'opportunities_company_search_by_industry_recent_search_items',
  OpportunitiesCompanySearchByNameCountryCode: 'opportunities_company_search_by_name_country_code',
  OpportunitiesCompanySearchByNameRecentSearchItems: 'opportunities_company_search_by_name_recent_search_items',
  OpportunitiesCrossSellByForm5500RecentSearchItems: 'opportunities_cross-sell_by_form5500_recent_search_items',
  OpportunitiesCrossSellRecentSearchItems: 'opportunities_cross-sell_recent_search_items',
  OpportunitiesLocationSearchRecentSearchItems: 'opportunities_location_search_recent_search_items',
  OpportunitiesMonolineForm5500RecentSearchItems: 'opportunities_monoline_by_form5500_recent_search_items',
  OpportunitiesMonolineRecentSearchItems: 'opportunities_monoline_recent_search_items',
  OpportunitiesPeopleSearchCountryCode: 'opportunities_people_search_country_code',
  OpportunitiesPeopleSearchRecentSearchItems: 'opportunities_people_search_recent_search_items',
  OpportunitiesRenewalsRecentSearchItems: 'opportunities_renewals_recent_search_items',
  OpportunitiesWinBackRecentSearchItems: 'opportunities_win_back_recent_search_items',
  // remove after recent / saved filters migration
  RenewalsPageTempPeriod: 'renewals_page_temp_period',
  WithDevServiceWorker: 'with_dev_service_worker',
  EnnablLoggerConfig: 'ennabl_logger_config',
  CarrierPdfDialogState: 'carrier_pdf_dialog_state',
  WhatsNewToastLastSeenDate: 'whats_new_toast_last_seen_date',
  WhatsNewPageData: 'whats_new_page_data',
} as const;

const universalKeys: LocalStorageKey[] = [
  'ennabl_logger_config',
  'with_dev_service_worker',
  'emulate_limit_error',
  'custom_limit_value',
  'auth0_token_cypress',
  'auth0_expires_in_cypress',
];

const getImpersonatedLocalStorageKeys = () => {
  return Object.fromEntries(
    Object.entries(localStorageKeyMap).map(([key, value]) => [
      key,
      universalKeys.includes(value) ? value : formatImpersonatedKey(impersonationToken, value),
    ])
  ) as typeof localStorageKeyMap;
};

export const localStorageKey = (() => {
  if (!impersonationToken) {
    return localStorageKeyMap;
  }

  trackImpersonatedSession();
  clearExpiredImpersonatedTokens();

  return getImpersonatedLocalStorageKeys();
})();

export type LocalStorageKey = ValueOf<typeof localStorageKeyMap>;
